/**
 * PrayerScheduleStrip — single-line band showing all 6 prayer times for
 * today. Inserted in the home below the service grid so the user gets
 * "full picture" of the day in zero taps (per user request: "lower band,
 * full line, all prayers with corresponding time for today").
 *
 * Layout:
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Fajr 05:04  Sunrise 06:37  Dhuhr 13:27  Asr 17:09  …    │
 *   └──────────────────────────────────────────────────────────┘
 *
 * - Past prayers shown faded.
 * - Next prayer highlighted (accent color + bold).
 * - Tabular nums + alignment columns (no dividers) per dense-display
 *   research (Bloomberg / Apple Watch Modular / Material 3 dense).
 * - Whole strip is a single <a href="#/prayer"> so a tap takes the user
 *   to the full prayer page.
 *
 * Self-hides when no location is set OR when prayer-time-service has no
 * data yet (fail-open, no broken UI).
 */

import type { AweniContext } from '../../types';
import { prayerTimeService } from '../../services/PrayerTimeService';
import { getLocationService } from '../../services/LocationService';

type PrayerKey = 'fajr' | 'sunrise' | 'dhuhr' | 'asr' | 'maghrib' | 'isha';
const ORDER: PrayerKey[] = ['fajr', 'sunrise', 'dhuhr', 'asr', 'maghrib', 'isha'];

export class PrayerScheduleStrip {
  private root: HTMLElement | null = null;
  private deps: { ctx: AweniContext };
  private locUnsub: (() => void) | null = null;
  private tick: ReturnType<typeof setInterval> | null = null;

  constructor(deps: { ctx: AweniContext }) {
    this.deps = deps;
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('section');
    root.className = 'aweni-prayer-strip';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('prayer_today_label') || "Today's prayers");
    parent.appendChild(root);
    this.root = root;
    this.render();

    this.locUnsub = getLocationService().onChange(() => this.render());
    // Re-render every minute — the "next" prayer marker shifts as time
    // passes; also serves as recovery if prayer service had no data on
    // initial mount but does after the server fetch resolves.
    this.tick = setInterval(() => this.render(), 60_000);
  }

  refresh(): void { this.render(); }

  unmount(): void {
    this.locUnsub?.();
    this.locUnsub = null;
    if (this.tick !== null) {
      clearInterval(this.tick);
      this.tick = null;
    }
    this.root?.remove();
    this.root = null;
  }

  private render(): void {
    if (!this.root) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const locale = this.deps.ctx.i18n.locale;

    const loc = getLocationService().getState();
    if (loc.lat === null || loc.lng === null) {
      this.root.style.display = 'none';
      return;
    }

    let data;
    try {
      data = prayerTimeService.getTodayTimes()
        ?? prayerTimeService.getTimesForCoords(loc.lat, loc.lng);
    } catch {
      this.root.style.display = 'none';
      return;
    }
    if (!data) {
      this.root.style.display = 'none';
      return;
    }

    this.root.style.display = '';
    const now = Date.now();
    const nextName = data.nextPrayer?.name as PrayerKey | undefined;

    const cells = ORDER.map((key) => {
      const time = data[key] as Date;
      if (!time) return '';
      const isPast = time.getTime() < now;
      const isNext = nextName === key;
      let timeStr = '';
      try {
        timeStr = new Intl.DateTimeFormat(locale, {
          hour: '2-digit', minute: '2-digit', hour12: false,
        }).format(time);
      } catch {
        timeStr = time.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
      }
      const name = prayerTimeService.getPrayerName(key, locale as 'en' | 'fr' | 'ar' | 'es' | 'de');
      const cls = `aweni-prayer-cell${isNext ? ' is-next' : ''}${isPast ? ' is-past' : ''}`;
      return `
        <span class="${cls}" role="listitem"
              aria-current="${isNext ? 'true' : 'false'}"
              aria-label="${escAttr(name)} ${escAttr(timeStr)}">
          <span class="aweni-prayer-name">${escHtml(name)}</span>
          <span class="aweni-prayer-time">${escHtml(timeStr)}</span>
        </span>
      `;
    }).filter(Boolean).join('');

    this.root.innerHTML = `
      <a href="#/prayer" class="aweni-prayer-strip-link"
         aria-label="${escAttr(t('prayer_today_label') || "Today's prayers")}">
        <span class="aweni-prayer-strip-row" role="list">${cells}</span>
      </a>
    `;
  }

  private injectStyles(): void {
    if (document.getElementById('aweni-prayer-strip-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-prayer-strip-styles';
    style.textContent = `
      .aweni-prayer-strip {
        margin: 0 0 16px;
      }
      .aweni-prayer-strip-link {
        display: block;
        background: var(--card-bg, #fff);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 14px;
        padding: 10px 12px;
        text-decoration: none;
        color: inherit;
        transition: border-color 120ms ease;
      }
      .aweni-prayer-strip-link:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-prayer-strip-link:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 2px;
      }
      .dark .aweni-prayer-strip-link {
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
      }
      .aweni-prayer-strip-row {
        display: grid;
        grid-template-columns: repeat(6, minmax(0, 1fr));
        gap: 4px;
        align-items: center;
      }
      .aweni-prayer-cell {
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        gap: 2px;
        padding: 4px 2px;
        border-radius: 8px;
        text-align: center;
        min-width: 0;
      }
      .aweni-prayer-cell.is-next {
        background: rgba(10,10,10,0.06);
      }
      .dark .aweni-prayer-cell.is-next {
        background: rgba(255,255,255,0.08);
      }
      .aweni-prayer-cell.is-past { opacity: 0.45; }
      .aweni-prayer-name {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.4px;
        color: var(--text-secondary, #6B7280);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100%;
      }
      .aweni-prayer-cell.is-next .aweni-prayer-name {
        color: var(--accent, #0A0A0A);
      }
      .aweni-prayer-time {
        font-size: 14px;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: var(--text-primary, #1E293B);
      }
      .dark .aweni-prayer-time { color: #F1F5F9; }
      .aweni-prayer-cell.is-next .aweni-prayer-time {
        color: var(--accent, #0A0A0A);
      }
      @media (max-width: 380px) {
        .aweni-prayer-name { font-size: 9px; letter-spacing: 0.3px; }
        .aweni-prayer-time { font-size: 13px; }
      }
    `;
    document.head.appendChild(style);
  }
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
